import { ref } from "vue";
import { PendingValue } from "@/types";
import { useRemoteControlStore } from "@/storeModules/remoteControl/remoteControlStore";
import { useGlobalConfigsStore } from "@/storeModules/globalConfigs/globalConfigs";

const pendingValuesMap = ref<Record<number, PendingValue[]>>({});

export function usePendingValues() {
  const getPendingValues = (deviceId: number) => pendingValuesMap.value[deviceId] || [];

  const setPendingValues = (deviceId: number, values: PendingValue[]) => {
    pendingValuesMap.value[deviceId] = values;
  };

  /**
   * Polls the getPendingValues endpoint until it returns an empty array.
   * Updates the pendingValuesMap on first call and when it's empty.
   */
  const pollPendingValues = (deviceId: number, intervalMs: number): Promise<void> => {
    let firstPoll = true;
    const remoteControlStore = useRemoteControlStore();
    const { connector } = useGlobalConfigsStore();

    return new Promise<void>((resolve) => {
      const interval = setInterval(async () => {
        try {
          if (!connector) return;

          const newPendingValues: PendingValue[] = await connector.devices.getPendingValues({
            deviceID: deviceId,
          });

          if (!Array.isArray(newPendingValues)) {
            clearInterval(interval);
            resolve();
            return;
          }

          setPendingValues(deviceId, newPendingValues);

          if (firstPoll && newPendingValues.length > 0) {
            firstPoll = false;
            remoteControlStore.closeRCModal();
          }

          // Stop polling if empty and update store
          if (newPendingValues.length === 0) {
            setPendingValues(deviceId, []);
            clearInterval(interval);
            resolve();
            return;
          }
        } catch (error) {
          console.error("Polling pending values failed:", error);
          clearInterval(interval);
          resolve();
        }
      }, intervalMs);
    });
  };

  return {
    getPendingValues,
    setPendingValues,
    pollPendingValues,
  };
}
